/**
 * Connect, the assistant: a text AI that lives in the team chat as user 0.
 * Its conversation is an ordinary chat channel (dm:0:<me>), so the chat store
 * holds the messages; this module only knows whether it is switched on, which
 * tools it has (for the suggestion chips) and whether it is thinking, and keeps
 * the list of files it made for me (Files: PDF reports, Excel files).
 */
import { shrinkImage } from "@/utils/shrinkImage";
import { saveAssistantFile } from "@/utils/assistantDocs";

const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Connect", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => {
    if (obj[k] !== undefined && obj[k] !== null) p.append(k, obj[k]);
  });
  return p;
};

const state = () => ({
  loaded: false,
  enabled: false,
  allowed: false, // given by hand (assistant.use): without it the panel shows no way in
  model: "",
  channel: "", // dm:0:<my uid>
  botUid: 0,
  tools: [], // [{ name, description, example, permission, write, allowed }]
  busy: false,
  skipConfirm: false, // an administrator turned "Ask before changes" off: their cards confirm themselves (_auto)
  canSkipConfirm: false, // administrators only
  needsApproval: false, // what this person confirms goes to an administrator first (assistant.needs_approval)
  files: [], // the PDF reports and Excel files Connect made for me (Files), newest first
  filesLoading: false,
  filesKeepDays: 30,
  filesGone: [], // ids deleted here or found gone: their cards under older replies say so at once
  models: [], // /model: [{ id, name, provider, note }], Gemini always, DeepSeek while the server has its key
  modelPick: "", // my pick ("" until loaded = the server's default)
  modelDefault: "",
  modelsLoading: false,
  modelProblems: {}, // by provider: { name, error, at }, its last failure while it has not answered since (Gemini answered)
  modelCanChoose: false, // /model is the administrators' (the server's can_choose); everybody else is on Gemini
});

const mutations = {
  SET_ASSISTANT_STATUS(s, d) {
    s.loaded = true;
    s.enabled = !!d.enabled;
    s.allowed = !!d.allowed;
    s.model = d.model || "";
    s.channel = d.channel || "";
    s.botUid = d.bot_uid || 0;
    s.tools = d.tools || [];
    s.skipConfirm = !!d.skip_confirm;
    s.canSkipConfirm = !!d.can_skip_confirm;
    s.needsApproval = !!d.needs_approval;
  },
  SET_ASSISTANT_BUSY(s, v) {
    s.busy = !!v;
  },
  SET_ASSISTANT_SKIP_CONFIRM(s, v) {
    s.skipConfirm = !!v;
  },
  SET_ASSISTANT_FILES(s, d) {
    s.files = d.files || [];
    if (d.keep_days) s.filesKeepDays = d.keep_days;
  },
  SET_ASSISTANT_FILES_LOADING(s, v) {
    s.filesLoading = !!v;
  },
  SET_ASSISTANT_MODELS(s, d) {
    s.models = d.models || [];
    s.modelPick = d.current || "";
    s.modelDefault = d.default || "";
    s.modelProblems = d.problems || {};
    s.modelCanChoose = d.can_choose !== false; // an older API sends no can_choose: everybody picked then
  },
  SET_ASSISTANT_MODEL(s, id) {
    s.modelPick = id;
  },
  SET_ASSISTANT_MODELS_LOADING(s, v) {
    s.modelsLoading = !!v;
  },
  DROP_ASSISTANT_FILE(s, id) {
    s.files = s.files.filter((f) => f.id !== id);
    if (s.filesGone.indexOf(id) === -1) s.filesGone.push(id);
  },
  RESET_ASSISTANT(s) {
    Object.assign(s, state());
  },
};

const getters = {
  // A handful of starters, not the whole catalogue: reads first, then one or two actions.
  assistantChips: (s) => {
    const ok = s.tools.filter((t) => t.allowed && t.example);
    return ok
      .filter((t) => !t.write)
      .slice(0, 5)
      .concat(ok.filter((t) => t.write).slice(0, 2))
      .map((t) => t.example);
  },
};

const actions = {
  async assistantStatus({ commit }) {
    try {
      const { data } = await window.$http.axios.get("assistant/status", {
        silent: true,
      });
      commit(
        "SET_ASSISTANT_STATUS",
        data.status ? data : { enabled: false, tools: [] },
      );
    } catch (e) {
      commit("SET_ASSISTANT_STATUS", { enabled: false, tools: [] });
    }
  },

  /** A question in the assistant's direct message. The API stores both the question and the answer. */
  async assistantAsk({ commit, dispatch }, { text, page, store }) {
    commit("SET_ASSISTANT_BUSY", true);
    const early = setTimeout(() => dispatch("chatPoll"), 700); // the question row exists before the model answers
    try {
      const { data } = await window.$http.axios.post(
        "assistant/message",
        form({ text, page: page || "", store: store || "" }),
      );
      clearTimeout(early);
      await dispatch("chatPoll");
      if (data.status) return data;
      notifyError(commit, data.message || "Connect could not answer.");
      return null;
    } catch (e) {
      clearTimeout(early);
      dispatch("chatPoll");
      notifyError(commit, "Connect could not answer just now.");
      return null;
    } finally {
      commit("SET_ASSISTANT_BUSY", false);
    }
  },

  // ---- Talking with Connect (voice, 2026-10-21): the browser talks to Gemini Live itself (utils/liveVoice);
  // the API gives the session, runs each tool the voice model calls as me, and keeps what was said in the chat.

  async assistantVoiceSession({ commit }, { page, store }) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/voice/session",
        form({ page: page || "", store: store || "" }),
      );
      if (data.status) return data;
      notifyError(commit, data.message || "Voice could not start.");
    } catch (e) {
      notifyError(commit, "Voice could not start just now.");
    }
    return null;
  },

  /** One call of the voice model: { status, result (for the model), route (a page it opens), shown (a chart or file put in the chat) }. */
  async assistantVoiceTool(
    { dispatch },
    { name, args, question, page, store },
  ) {
    const { data } = await window.$http.axios.post(
      "assistant/voice/tool",
      form({
        name,
        args: JSON.stringify(args || {}),
        question: question || "",
        page: page || "",
        store: store || "",
      }),
      { silent: true },
    );
    if (data.shown) dispatch("chatPoll");
    return data;
  },

  /** What was said, into the chat: my words as mine, Connect's as its own. */
  async assistantVoiceSave({ dispatch }, { user, model }) {
    const turns = [];
    if (user) turns.push({ role: "user", text: user });
    if (model) turns.push({ role: "model", text: model });
    if (!turns.length) return;
    await window.$http.axios.post(
      "assistant/voice/turns",
      form({ turns: JSON.stringify(turns) }),
      { silent: true },
    );
    dispatch("chatPoll");
  },

  /**
   * "@connect …" was sent in a channel; the answer lands there through the normal poll. In an
   * encrypted channel the server holds ciphertext, so the plain text and the recent conversation
   * (decrypted here, see the chatContext getter) travel with the call. The bot answers in plain text.
   */
  async assistantMention({ dispatch }, { channel, id, text, context }) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/mention",
        form({
          channel,
          message_id: id,
          text: text || "",
          context: context || "",
        }),
      );
      dispatch("chatPoll");
      return data;
    } catch (e) {
      return null;
    }
  },

  /**
   * Confirm or cancel a proposal card. The outcome arrives as a bot message through the poll. A
   * card that deletes things goes with decision confirm_delete and the number the person typed.
   */
  async assistantExecute({ commit, dispatch }, { id, decision, typed }) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/execute",
        form({ id, decision, typed: typed || "" }),
      );
      if (data.req_status)
        commit("PATCH_CHAT_MESSAGE", {
          id,
          patch: { req_status: data.req_status },
        });
      if (!data.status && data.message) notifyError(commit, data.message);
      dispatch("chatPoll");
      return data;
    } catch (e) {
      notifyError(commit, "Could not do that just now.");
      return null;
    }
  },

  /** "Ask before changes" on or off (administrators): off, the cards Connect prepares confirm themselves. */
  async assistantSetSkipConfirm({ commit }, skip) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/settings",
        form({ skip_confirm: skip ? 1 : 0 }),
      );
      if (!data.status) {
        notifyError(commit, data.message || "Could not change that.");
        return false;
      }
      commit("SET_ASSISTANT_SKIP_CONFIRM", data.skip_confirm);
      return true;
    } catch (e) {
      notifyError(commit, "Could not change that just now.");
      return false;
    }
  },

  /** The paperclip: upload the image, then tell Connect about it as a message. */
  async assistantUpload({ commit, dispatch }, { file, page, store }) {
    commit("SET_ASSISTANT_BUSY", true);
    // A phone photo is often more than the API takes: a big one goes as a smaller JPEG copy.
    const image = await shrinkImage(file).catch(() => file);
    const fd = new FormData();
    fd.append("image", image);
    try {
      const { data } = await window.$http.axios.post("assistant/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.status) {
        notifyError(commit, data.message || "The upload failed.");
        return null;
      }
      commit("SET_ASSISTANT_BUSY", false);
      return await dispatch("assistantAsk", {
        text: "[image] " + data.url,
        page,
        store,
      });
    } catch (e) {
      notifyError(commit, "The upload failed.");
      return null;
    } finally {
      commit("SET_ASSISTANT_BUSY", false);
    }
  },

  // ---- menu change sets (server: classes/MenuOps). A card whose proposal carries cs=<id>.

  /** What a change-set card holds and how it went. */
  async assistantChangeset(_, id) {
    try {
      const { data } = await window.$http.axios.get("assistant/changeset", {
        params: { id },
      });
      return data && data.status ? data.data : null;
    } catch (e) {
      return null;
    }
  },

  /** One store of a running sync. The card calls it until the sync is finished. */
  async assistantChangesetStep({ commit }, id) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/changeset/step",
        form({ id }),
      );
      if (!data.status && data.message) notifyError(commit, data.message);
      return data;
    } catch (e) {
      return { status: false, message: "The sync step did not answer." };
    }
  },

  /** Queue a sync's failed pushes again. */
  async assistantChangesetRetry({ commit }, id) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/changeset/retry",
        form({ id }),
      );
      if (!data.status && data.message) notifyError(commit, data.message);
      return data;
    } catch (e) {
      notifyError(commit, "Could not retry just now.");
      return null;
    }
  },

  /** After "Try at one store first": apply the stores held back. The server closes the card and posts the result. */
  async assistantChangesetContinue({ commit, dispatch }, id) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/changeset/continue",
        form({ id }),
      );
      if (!data.status)
        notifyError(commit, data.message || "Could not apply the rest.");
      dispatch("chatPoll");
      return data;
    } catch (e) {
      notifyError(commit, "Could not apply the rest just now.");
      return null;
    }
  },

  /** "Prepare sync" / "Undo" on a done card: the server posts a new card for it. */
  async assistantChangesetFollowUp({ commit, dispatch }, { id, what }) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/changeset/followup",
        form({ id, what }),
      );
      if (!data.status) {
        notifyError(commit, data.message || "Could not prepare that.");
        return null;
      }
      dispatch("chatPoll");
      return data;
    } catch (e) {
      notifyError(commit, "Could not prepare that just now.");
      return null;
    }
  },

  /** "/undo" or "/sync" typed in the chat: the server posts the card for my latest applied change. */
  async assistantChangesetLatest({ commit, dispatch }, what) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/changeset/latest",
        form({ what }),
      );
      if (!data.status) {
        notifyError(commit, data.message || "Could not prepare that.");
        return null;
      }
      dispatch("chatPoll");
      return data;
    } catch (e) {
      notifyError(commit, "Could not prepare that just now.");
      return null;
    }
  },

  /** "/clear": wipe my conversation with Connect on the server, then locally. */
  async assistantClear({ commit, state, rootState }) {
    const channel =
      state.channel ||
      (rootState.chat.me ? "dm:0:" + rootState.chat.me.uid : "");
    try {
      const { data } = await window.$http.axios.post("assistant/clear");
      if (!data.status) {
        notifyError(
          commit,
          data.message || "Could not clear the conversation.",
        );
        return false;
      }
      if (channel) commit("CLEAR_CHAT_CHANNEL", channel);
      return true;
    } catch (e) {
      notifyError(commit, "Could not clear the conversation.");
      return false;
    }
  },

  // ---- files Connect made (PDF reports, Excel files; server: classes/Assistant/AssistantFiles). Only my own.

  /** Files: newest first; the server drops the expired ones on the way. */
  async assistantFiles({ commit }) {
    commit("SET_ASSISTANT_FILES_LOADING", true);
    try {
      const { data } = await window.$http.axios.get("assistant/files", {
        silent: true,
      });
      if (data && data.status) commit("SET_ASSISTANT_FILES", data);
      else
        notifyError(
          commit,
          (data && data.message) || "Could not load your files.",
        );
    } catch (e) {
      notifyError(commit, "Could not load your files just now.");
    } finally {
      commit("SET_ASSISTANT_FILES_LOADING", false);
    }
  },

  /** Build one file from the document the server keeps and save it. false: the file is not kept any more. */
  async assistantFileDownload({ commit }, file) {
    let data;
    try {
      ({ data } = await window.$http.axios.get("assistant/file", {
        params: { id: file.id },
        silent: true,
      }));
    } catch (e) {
      notifyError(commit, "Could not get the file just now.");
      return null;
    }
    if (!data || !data.status) {
      notifyError(
        commit,
        (data && data.message) || "That file is not there any more.",
      );
      commit("DROP_ASSISTANT_FILE", file.id);
      return false;
    }
    try {
      await saveAssistantFile(data.file, data.document);
      return true;
    } catch (e) {
      notifyError(commit, "Could not make the file in this browser.");
      return null;
    }
  },

  async assistantFileDelete({ commit }, id) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/file/delete",
        form({ id }),
      );
      if (!data.status) {
        notifyError(commit, data.message || "Could not delete that file.");
        return false;
      }
      commit("DROP_ASSISTANT_FILE", id);
      return true;
    } catch (e) {
      notifyError(commit, "Could not delete that file just now.");
      return false;
    }
  },

  // ---- /model: which model answers me (server: classes/Assistant/Models). Only my own pick.

  async assistantModels({ commit }) {
    commit("SET_ASSISTANT_MODELS_LOADING", true);
    try {
      const { data } = await window.$http.axios.get("assistant/models", {
        silent: true,
      });
      if (data && data.status) commit("SET_ASSISTANT_MODELS", data);
    } catch (e) {
      /* an older API has no /models: the picker stays empty and Gemini answers as before */
    } finally {
      commit("SET_ASSISTANT_MODELS_LOADING", false);
    }
  },

  async assistantSetModel({ commit }, id) {
    try {
      const { data } = await window.$http.axios.post(
        "assistant/model",
        form({ id }),
      );
      if (!data.status) {
        notifyError(commit, data.message || "Could not change the model.");
        return false;
      }
      commit("SET_ASSISTANT_MODEL", data.current);
      return true;
    } catch (e) {
      notifyError(commit, "Could not change the model just now.");
      return false;
    }
  },

  assistantReset({ commit }) {
    commit("RESET_ASSISTANT");
  },
};

export default { state, getters, mutations, actions };
